import { readTokens } from './token-store';

/** Claims that change on every refresh of the *same* session. Excluding them is
 *  what makes a rotation invisible here — the whole point of deriving identity
 *  rather than watching the token string. */
const volatileClaims = new Set(['exp', 'iat', 'nbf', 'jti', 'auth_time', 'nonce']);

/** The token isn't a JWT we can read. Never treated as an identity change: a
 *  token-format change must not clear every tab's cache on every refresh. */
export const opaqueIdentity = 'opaque';
export type SessionIdentity = string | null;
const decodePayload = (token: string): Record<string, unknown> | null => {
  const segments = token.split('.');
  const payloadSegment = segments.length === 3 ? segments[1] : undefined;
  if (!payloadSegment) return null;
  try {
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
    const bytes = Uint8Array.from(atob(padded), c => c.charCodeAt(0));
    // TextDecoder rather than atob alone, so non-ASCII claim values (Icelandic
    // names, say) survive intact instead of arriving as mojibake.
    const parsed = JSON.parse(new TextDecoder().decode(bytes));
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Who this token says is signed in — a cache-partition key, nothing more.
 *
 * The signature is NOT verified and must never be trusted for authorisation.
 * This exists only so a tab can tell "the same customer's token was rotated"
 * (keep the cache) from "a different customer signed in, or signed out"
 * (drop it), without every sign-in path having to remember to announce itself.
 *
 * Built from the whole payload minus the volatile claims rather than from one
 * named claim, so it doesn't depend on guessing which field the backend uses
 * for the customer.
 */
export function deriveSessionIdentity(accessToken: string | null | undefined): SessionIdentity {
  if (!accessToken) return null;
  const payload = decodePayload(accessToken);
  if (!payload) return opaqueIdentity;
  const stable = Object.keys(payload).filter(key => !volatileClaims.has(key)).sort().map(key => `${key}=${JSON.stringify(payload[key])}`).join('&');
  return stable || opaqueIdentity;
}

/** The identity currently in storage. */
export const currentSessionIdentity = (): SessionIdentity => deriveSessionIdentity(readTokens()?.accessToken);